import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { usePlan } from './billing';
import type { Page } from './types';

export type AccountCategory = 'STUDENT' | 'FACULTY' | 'STAFF';
export type DepartureCategory = 'ALUMNI' | 'PAST_STUDENT' | 'PAST_STAFF';
export type RoleCategory = AccountCategory | DepartureCategory;
export type GoogleAccountStatus = 'PENDING' | 'SKIPPED' | 'ACTIVE' | 'DEPARTED' | 'SUSPENDED';
export type ConflictStrategy = 'PENDING' | 'NUMBER' | 'INITIALS' | 'INITIALS_THEN_NUMBER';
export type PlanAction = 'CREATE' | 'LINK' | 'PENDING' | 'MOVE' | 'RETURN' | 'DEPART' | 'SUSPEND' | 'DROP' | 'HELD' | 'FAIL';

export const ROLE_CATEGORY_LABEL: Record<RoleCategory, string> = {
  STUDENT: 'Student account',
  FACULTY: 'Faculty account',
  STAFF: 'Staff account',
  ALUMNI: 'Alumni (left)',
  PAST_STUDENT: 'Past student (left)',
  PAST_STAFF: 'Past faculty or staff (left)',
};

export const CATEGORY_LABEL: Record<AccountCategory, string> = { STUDENT: 'Student', FACULTY: 'Faculty', STAFF: 'Staff' };

export const DEPARTURE_LABEL: Record<DepartureCategory, string> = { ALUMNI: 'Alumni', PAST_STUDENT: 'Past student', PAST_STAFF: 'Past faculty or staff' };

export const STATUS_LABEL: Record<GoogleAccountStatus, string> = {
  PENDING: 'Needs an address',
  SKIPPED: 'Skipped',
  ACTIVE: 'Active',
  DEPARTED: 'Departed',
  SUSPENDED: 'Suspended',
};

export const STATUS_TONE: Record<GoogleAccountStatus, 'amber' | 'gray' | 'green' | 'violet' | 'red'> = {
  PENDING: 'amber',
  SKIPPED: 'gray',
  ACTIVE: 'green',
  DEPARTED: 'violet',
  SUSPENDED: 'red',
};

export const PLAN_LABEL: Record<PlanAction, string> = {
  CREATE: 'Create',
  LINK: 'Match',
  PENDING: 'Needs an address',
  MOVE: 'Move',
  RETURN: 'Returning',
  DEPART: 'Departed',
  SUSPEND: 'Suspend',
  DROP: 'Remove from list',
  HELD: 'Held back',
  FAIL: 'Failed',
};

export const PLAN_TONE: Record<PlanAction, 'green' | 'blue' | 'amber' | 'brand' | 'violet' | 'red' | 'gray'> = {
  CREATE: 'green',
  LINK: 'blue',
  PENDING: 'amber',
  MOVE: 'brand',
  RETURN: 'green',
  DEPART: 'violet',
  SUSPEND: 'red',
  DROP: 'gray',
  HELD: 'amber',
  FAIL: 'red',
};

export const CONFLICT_OPTIONS: { value: ConflictStrategy; title: string; text: string }[] = [
  { value: 'PENDING', title: 'Ask us', text: 'They wait under Pending users until someone chooses their address.' },
  { value: 'INITIALS', title: 'Use more of the first name', text: 'Only when the convention has {firstInitial}. Anyone still left over waits under Pending users.' },
  { value: 'NUMBER', title: 'Add a number', text: 'The first free address with 2, 3, 4… on the end.' },
  { value: 'INITIALS_THEN_NUMBER', title: 'More of the first name, then a number', text: 'Longer initials first, then a number if those are taken too.' },
];

export interface GoogleProvisioningSettings {
  adminEmail?: string;
  customerId: string;
  roles: { roleId: string; roleName: string; category: RoleCategory }[];
  studentDomain?: string;
  facultyDomain?: string;
  staffDomain?: string;
  studentNaming: string;
  gradeNaming: Record<string, string>;
  facultyNaming: string;
  staffNaming: string;
  usePreferredName: boolean;
  conflictStrategy: ConflictStrategy;
  studentOrgUnit?: string;
  classOrgUnits: Record<string, string>;
  facultyOrgUnit?: string;
  staffOrgUnit?: string;
  alumniOrgUnit?: string;
  pastStudentOrgUnit?: string;
  pastStaffOrgUnit?: string;
  suspendDeparted: boolean;
  suspendAfterDays: number;
  manageExistingAccounts: boolean;
  /** Off: a sync works every change out and carries none of them out until someone approves it. */
  autoApply: boolean;
  changePasswordAtNextLogin: boolean;
  maxNewAccounts: number;
  departureSafetyPercent: number;
  technicianIds: string[];
}

/** One thing a provisioning sync would do, waiting for someone to approve it. */
export interface ProvisioningChange {
  id: string;
  action: 'CREATE' | 'LINK' | 'MOVE' | 'RETURN' | 'DEPART' | 'SUSPEND' | 'DROP';
  label: string;
  name: string;
  category: AccountCategory;
  email: string | null;
  orgUnitPath: string | null;
  detail: string | null;
  lastError: string | null;
  /** A create whose address is taken: whoever approves it chooses one. */
  needsEmail: boolean;
  plannedAt: string;
}

export interface GoogleAccount {
  id: string;
  blackbaudUserId: string;
  personId: string | null;
  firstName: string;
  lastName: string;
  category: AccountCategory;
  gradYear: number | null;
  gradeLevel: string | null;
  status: GoogleAccountStatus;
  origin: 'CREATED' | 'LINKED' | null;
  email: string | null;
  orgUnitPath: string | null;
  suggestedEmail: string | null;
  pendingReason: string | null;
  departedAs: DepartureCategory | null;
  departedAt: string | null;
  suspendAt: string | null;
  suspendedAt: string | null;
  lastError: string | null;
  namedByName: string | null;
  hasPassword: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProvisioningOptions {
  roles: { id: string; name: string; suggested: RoleCategory | null }[];
  rolesError: string | null;
  orgUnits: string[];
  orgUnitsError: string | null;
  classes: number[];
  grades: string[];
  technicians: { id: string; name: string; email: string }[];
  domains: string[];
}

export interface PlanItem {
  action: PlanAction;
  blackbaudUserId: string;
  name: string;
  category: AccountCategory;
  email: string | null;
  orgUnitPath: string | null;
  detail: string | null;
}

export interface ProvisioningPreview {
  stats: Record<string, number>;
  plan: PlanItem[];
  log: { at: string; level: 'info' | 'warn' | 'error'; message: string }[];
}

export type GoogleAccountPage = Page<GoogleAccount>;

export const useProvisioningAccess = (enabled = true) =>
  useQuery({
    queryKey: ['google-provisioning', 'access'],
    queryFn: () => api.get<{ canUse: boolean; canConfigure: boolean }>('/google-provisioning/access'),
    enabled,
    staleTime: 30_000,
  });

export function useProvisioningOptions(enabled: boolean) {
  // Provisioning is an add-on: without it the options aren't available to load.
  const plan = usePlan();
  return useQuery({
    queryKey: ['google-provisioning', 'options'],
    queryFn: () => api.get<ProvisioningOptions>('/google-provisioning/options'),
    enabled: enabled && plan.has('GOOGLE_PROVISIONING'),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Naming conventions: the same rules as the server, for the examples on the settings page
// ---------------------------------------------------------------------------

export const NAMING_VARIABLES = [
  { token: '{firstName}', label: 'First name' },
  { token: '{firstInitial}', label: 'First initial' },
  { token: '{lastName}', label: 'Last name' },
  { token: '{userId}', label: 'User ID' },
  { token: '{gradYear}', label: 'Class year' },
  { token: '{gradYY}', label: 'Class year (2 digits)' },
] as const;

const VARIABLES = ['firstName', 'lastName', 'firstInitial', 'userId', 'gradYear', 'gradYY'];
const TOKEN = /\{([a-zA-Z]+)\}/g;
const variableOf = (token: string) => VARIABLES.find((name) => name.toLowerCase() === token.toLowerCase());

export function templateProblem(template: string): string | null {
  const value = template.trim();
  if (!value) return 'Enter a naming convention';
  const used = new Set<string>();
  for (const match of value.matchAll(TOKEN)) {
    const variable = variableOf(match[1]);
    if (!variable) return `{${match[1]}} isn’t a variable`;
    used.add(variable);
  }
  const literal = value.replace(TOKEN, '');
  if (/[{}]/.test(literal)) return 'Close every { with a }';
  if (!/^[a-zA-Z0-9._-]*$/.test(literal)) return 'Outside the variables, use only letters, numbers, periods, dashes, and underscores';
  if (!['firstName', 'lastName', 'firstInitial', 'userId'].some((name) => used.has(name))) return 'Include the person’s name or user ID';
  return null;
}

const letters = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const clean = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '')
    .replace(/([._-])[._-]+/g, '$1')
    .replace(/^[._-]+|[._-]+$/g, '')
    .slice(0, 64);

interface ExamplePerson {
  firstName: string;
  lastName: string;
  userId: string;
  gradYear: number;
}

function render(template: string, person: ExamplePerson, initials = 1) {
  const first = letters(person.firstName);
  const values: Record<string, string> = {
    firstName: first,
    lastName: letters(person.lastName),
    firstInitial: first.slice(0, initials),
    userId: letters(person.userId),
    gradYear: String(person.gradYear),
    gradYY: String(person.gradYear).slice(-2),
  };
  return clean(template.trim().replace(TOKEN, (_, token: string) => values[variableOf(token) ?? ''] ?? '')) || null;
}

/** The addresses the rules would try for someone, in order: the first is the convention's own. */
export function exampleAddresses(template: string, strategy: ConflictStrategy, person: ExamplePerson, count = 4) {
  if (templateProblem(template)) return [];
  const base = render(template, person);
  if (!base) return [];
  const out = [base];
  if ((strategy === 'INITIALS' || strategy === 'INITIALS_THEN_NUMBER') && /\{firstinitial\}/i.test(template)) {
    for (let n = 2; n <= letters(person.firstName).length; n++) {
      const next = render(template, person, n);
      if (next && !out.includes(next)) out.push(next);
    }
  }
  if (strategy === 'NUMBER' || strategy === 'INITIALS_THEN_NUMBER') for (let n = 2; n < 2 + count; n++) out.push(`${base}${n}`);
  return out.slice(0, count + 1);
}

export const SAMPLE_STUDENT: ExamplePerson = { firstName: 'Sally', lastName: 'Hansen', userId: '4100123', gradYear: new Date().getFullYear() + 4 };
export const SAMPLE_SIBLING: ExamplePerson = { firstName: 'Sam', lastName: 'Hansen', userId: '4100456', gradYear: new Date().getFullYear() + 6 };
export const SAMPLE_TEACHER: ExamplePerson = { firstName: 'María', lastName: 'López', userId: '3200789', gradYear: 0 };
